import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, Wand2 } from 'lucide-react';
import { btnFieldAction } from './constants';
import { LockTip } from '../shared/FormKit';
import { extractSigningDate, firstPdf, renderPdfPage } from '../../utils/fsDateExtract';

// "Auto-detect from PDF" for the Audit report signing date field
// (docs/modules/workflow-fs.md §5). Shared by the New form and the Detail
// Modify modal. Returns { busy, button, panel }: mount `button` in the
// <Field action> slot and `panel` right under the date input. While a scan
// runs the page should disable file pickers and Submit/Save (via `busy`),
// and this hook arms a beforeunload guard so closing the tab prompts first.
//
// A hit also renders the page it came from (date highlighted) under the
// message, so the person can check the proposal against the signature block
// without opening the PDF. The preview is a blob URL owned by this hook —
// revoked whenever it is replaced or the hook unmounts.
export const useFsAutoDetect = ({ files, onDetected, disabled = false }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null); // { url, page } | null
  const [showPreview, setShowPreview] = useState(true);
  const cancelRef = useRef(false);

  const pdf = firstPdf(files);

  // A new file selection invalidates any previous outcome message.
  useEffect(() => { setResult(null); setPreview(null); }, [files]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  // Abort a scan the page navigates away from (unmount = cancel, harmless).
  useEffect(() => () => { cancelRef.current = true; }, []);

  useEffect(() => {
    if (!busy) return undefined;
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [busy]);

  const run = async () => {
    if (!pdf || busy) return;
    cancelRef.current = false;
    setBusy(true);
    setProgress(null);
    setResult(null);
    setPreview(null);
    try {
      const res = await extractSigningDate(pdf, {
        onProgress: setProgress,
        isCancelled: () => cancelRef.current,
      });
      if (res.status !== 'cancelled') {
        setResult(res);
        if (res.status === 'found') {
          onDetected(res.date);
          // Best effort: the date is already delivered; a preview failure
          // leaves only the text message.
          try {
            const { blob } = await renderPdfPage(pdf, res.page, { highlight: res.raw });
            if (!cancelRef.current) {
              setPreview({ url: URL.createObjectURL(blob), page: res.page });
              setShowPreview(true);
            }
          } catch { /* preview is optional */ }
        }
      }
    } catch {
      setResult({ status: 'unreadable' });
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <LockTip text={pdf ? null : 'Select a PDF to enable auto-detection'}>
      <button
        type="button"
        onClick={run}
        disabled={disabled || busy || !pdf}
        className={btnFieldAction}
      >
        {busy
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Scanning…</>
          : <><Wand2 className="w-3.5 h-3.5" />Auto-detect from PDF</>}
      </button>
    </LockTip>
  );

  const panel = (
    <>
      {busy && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
          <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
          <span>
            Analysing the PDF — please keep this page open.
            {progress && ` Scanning page ${progress.scanned} of ${progress.total}…`}
          </span>
          <button
            type="button"
            onClick={() => { cancelRef.current = true; }}
            className="ml-auto shrink-0 text-xs font-medium underline hover:text-blue-900"
          >
            Cancel
          </button>
        </div>
      )}
      {!busy && result?.status === 'found' && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <div className="flex items-center gap-3">
            <span>Detected from page {result.page}: "{result.raw}". Please verify before submitting.</span>
            {preview && (
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="ml-auto shrink-0 text-xs font-medium underline hover:text-green-900"
              >
                {showPreview ? 'Hide Page' : `Show Page ${preview.page}`}
              </button>
            )}
          </div>
          {preview && showPreview && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-green-700 mb-1">
                <span>Page {preview.page} of the uploaded PDF — the detected date is highlighted.</span>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 underline hover:text-green-900"
                >
                  <ExternalLink className="w-3 h-3" />Open Full Size
                </a>
              </div>
              <div className="bg-white border border-green-200 rounded-md overflow-auto max-h-[70vh]">
                <img
                  src={preview.url}
                  alt={`Page ${preview.page} of the uploaded PDF`}
                  className="w-full h-auto block"
                />
              </div>
            </div>
          )}
        </div>
      )}
      {!busy && result?.status === 'no_text' && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          This PDF has no readable text layer (it may be a scanned copy). Auto-detection
          is not possible — please enter the signing date manually from the audit report.
        </div>
      )}
      {!busy && result?.status === 'not_found' && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <p>Could not identify the signing date in the auditor's report. Please enter it manually.</p>
          {result.rejected && (
            <p className="mt-1 text-xs">
              A date "{result.rejected.raw}" was found on page {result.rejected.page} but is
              inconsistent with the financial year end stated in the report, so it was not used.
            </p>
          )}
        </div>
      )}
      {!busy && result?.status === 'unreadable' && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Unable to open this PDF (the file may be corrupted or password-protected).
          Please enter the date manually.
        </div>
      )}
    </>
  );

  // `result` and `pdf` are exposed for the AI review (FsAiReview.js): its
  // condition 1 is "the auto-detect found the date", and the review renders
  // the hit page again from the same file.
  return { busy, button, panel, result, pdf };
};

export default useFsAutoDetect;
